/**
 * --------------------------------------------------------------------------
 * StickyGroup Web Component
 * --------------------------------------------------------------------------
 * Provides smooth sticky scrolling behavior for group elements.
 * The element scrolls normally until it reaches its original position,
 * then becomes sticky. When scrolling back up, it smoothly returns to
 * its original position without causing page jumps.
 * The element uses CSS sticky positioning to remain in document flow.
 */

if (!customElements.get('sticky-group')) {
  class StickyGroup extends HTMLElement {
    static CONSTANTS = {
      SCROLL_DEBOUNCE_DELAY: 10, // ms
      TRANSITION_DURATION: 300, // ms
      RAF_THRESHOLD: 16, // px - minimum scroll movement to trigger RAF logic
      SHOW_TIMEOUT: 150, // ms - timeout before showing element when scrolling up
    };

    constructor() {
      super();

      // Configurable parameters from attributes
      this.rafThreshold = parseInt(this.getAttribute('raf-threshold')) || StickyGroup.CONSTANTS.RAF_THRESHOLD;
      this.showTimeoutDelay = parseInt(this.getAttribute('show-delay')) || StickyGroup.CONSTANTS.SHOW_TIMEOUT;

      this.originalTop = 0;
      this.elementHeight = 0;
      this.currentScrollTop = 0;
      this.isSticky = false;
      this.isVisible = false;
      this.rafId = null;
      this.onScrollHandler = null;
      this.resizeHandler = null;
      this.stickyParent = null; // Parent .shopify-section element
      this.showTimeout = null; // Timeout for showing element when scrolling back up
      this.lastRafScrollTop = 0; // Scroll position from last RAF frame
      this.positionRafId = null; // RAF ID for position tracking loop

      // Check for reduced motion preference
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Touch/momentum scroll detection
      this.touchStartY = 0;
      this.isMomentumScrolling = false;
      this.lastTouchEnd = 0;
    }

    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    // --------------------------------------------------------------------
    // INITIALIZATION & CLEANUP
    // --------------------------------------------------------------------

    /**
     * Initializes the component by storing the original position and setting up scroll handlers.
     */
    initialize() {
      // Wait for next frame to ensure layout is complete
      requestAnimationFrame(() => {
        this.storeOriginalPosition();
        
        // Set up scroll handler with debouncing
        this.onScrollHandler = this.debounce(
          this.onScroll.bind(this),
          StickyGroup.CONSTANTS.SCROLL_DEBOUNCE_DELAY
        );
        
        this.resizeHandler = this.debounce(
          this.handleResize.bind(this),
          150
        );
        
        window.addEventListener('scroll', this.onScrollHandler, { passive: true });
        window.addEventListener('resize', this.resizeHandler, { passive: true });

        // Touch event listeners for momentum scroll detection
        this.touchStartHandler = this.handleTouchStart.bind(this);
        this.touchEndHandler = this.handleTouchEnd.bind(this);
        window.addEventListener('touchstart', this.touchStartHandler, { passive: true });
        window.addEventListener('touchend', this.touchEndHandler, { passive: true });

        // Start continuous position tracking loop
        this.startPositionTracking();

        // Initial check
        this.onScroll();
      });
    }

    /**
     * Stores the original position and height of the element in the document flow.
     */
    storeOriginalPosition() {
      try {
        const rect = this.getBoundingClientRect();
        const currentScrollTop = this.getActualScrollTop();
        this.originalTop = rect.top + currentScrollTop;
        this.elementHeight = rect.height;

        // Initialize RAF debouncing scroll position
        this.lastRafScrollTop = currentScrollTop;

        // Find parent .shopify-section element if not already found
        if (!this.stickyParent) {
          let parent = this.parentElement;
          while (parent && parent !== document.body) {
            if (parent && parent.classList && parent.classList.contains('shopify-section')) {
              this.stickyParent = parent;
              break;
            }
            parent = parent.parentElement;
          }
        }
      } catch (error) {
        // Silently handle errors during position calculation
        this.originalTop = 0;
        this.elementHeight = 0;
      }
    }

    /**
     * Starts the continuous position tracking RAF loop.
     * Updates --sticky-group-bottom with the element's actual bottom position from viewport top.
     */
    startPositionTracking() {
      const updatePosition = () => {
        try {
          const rect = this.getBoundingClientRect();
          // Report the bottom position of the element from the top of the viewport
          const bottomFromTop = Math.floor(rect.bottom);
          document.documentElement.style.setProperty('--sticky-group-bottom', `${Math.max(0, bottomFromTop)}px`);
        } catch (error) {
          // Silently handle errors
        }
        this.positionRafId = requestAnimationFrame(updatePosition);
      };

      this.positionRafId = requestAnimationFrame(updatePosition);
    }

    /**
     * Stops the position tracking RAF loop.
     */
    stopPositionTracking() {
      if (this.positionRafId) {
        cancelAnimationFrame(this.positionRafId);
        this.positionRafId = null;
      }
    }

    /**
     * Cleans up event listeners.
     */
    cleanup() {
      try {
        if (this.onScrollHandler) {
          window.removeEventListener('scroll', this.onScrollHandler);
        }
        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
        }
        if (this.touchStartHandler) {
          window.removeEventListener('touchstart', this.touchStartHandler);
        }
        if (this.touchEndHandler) {
          window.removeEventListener('touchend', this.touchEndHandler);
        }
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
        }
        this.stopPositionTracking();
        if (this.showTimeout) {
          clearTimeout(this.showTimeout);
          this.showTimeout = null;
        }
      } catch (error) {
        // Silently handle cleanup errors
      }
    }

    /**
     * Handles window resize events by recalculating the original position and height.
     */
    handleResize() {
      this.storeOriginalPosition();
      this.onScroll();
    }

    /**
     * Handles touch start events for momentum scroll detection.
     */
    handleTouchStart(event) {
      this.touchStartY = event.touches[0].clientY;
      this.isMomentumScrolling = false;
    }

    /**
     * Handles touch end events for momentum scroll detection.
     */
    handleTouchEnd(event) {
      this.lastTouchEnd = Date.now();
      // Detect momentum scrolling by checking if scroll continues after touch ends
      setTimeout(() => {
        if (Date.now() - this.lastTouchEnd > 100) {
          this.isMomentumScrolling = true;
        }
      }, 50);
    }

    // --------------------------------------------------------------------
    // SCROLL HANDLING
    // --------------------------------------------------------------------

    /**
     * Gets the actual scroll position, accounting for scroll lock from dialogs.
     * When body is position:fixed, window.pageYOffset returns 0, so we extract
     * the true scroll position from body.style.top or data-scroll-position attribute.
     */
    getActualScrollTop() {
      try {
        // Check if scroll is locked (body has position: fixed)
        const bodyStyle = window.getComputedStyle(document.body);
        const isScrollLocked = bodyStyle.position === 'fixed';

        if (isScrollLocked) {
          // Extract scroll position from body's top style (e.g., "-1200px" -> 1200)
          const topValue = document.body.style.top;
          if (topValue) {
            const match = topValue.match(/-?(\d+(?:\.\d+)?)px/);
            if (match) {
              return Math.abs(parseFloat(match[1]));
            }
          }

          // Fallback to data attribute stored by dialog-popover
          const scrollPositionAttr = document.body.getAttribute('data-scroll-position');
          if (scrollPositionAttr) {
            return parseFloat(scrollPositionAttr) || 0;
          }

          // If we can't determine, return 0
          return 0;
        }

        // Normal scroll - use standard methods
        return window.pageYOffset || document.documentElement.scrollTop || 0;
      } catch (error) {
        // Fallback to standard method on error
        return window.pageYOffset || document.documentElement.scrollTop || 0;
      }
    }

    /**
     * Handles scroll events to determine when to apply or remove sticky behavior.
     */
    onScroll() {
      if (this.rafId) return;

      this.rafId = requestAnimationFrame(() => {
        const scrollTop = this.getActualScrollTop();
        const scrollDelta = scrollTop - this.currentScrollTop;
        const scrollingDown = scrollDelta > 0;

        // RAF-based debouncing: check if scroll has moved more than threshold since last RAF
        const scrollMovement = Math.abs(scrollTop - this.lastRafScrollTop);
        const shouldTriggerLogic = scrollMovement > this.rafThreshold;

        if (shouldTriggerLogic) {
          // Calculate valid scroll bounds to handle bounce scenarios
          const maxScroll = Math.max(
            document.documentElement.scrollHeight - document.documentElement.clientHeight,
            document.body.scrollHeight - document.body.clientHeight
          );
          const minScroll = 0;
          const isValidScrollPosition = scrollTop >= minScroll && scrollTop <= maxScroll || this.isMomentumScrolling;

          // Calculate element's end position (where the element ends in document flow)
          const elementEndPosition = this.originalTop + this.elementHeight;

          // Only activate sticky class when the element is fully hidden (off-screen)
          const shouldBeSticky = isValidScrollPosition && scrollTop >= elementEndPosition;

          if (shouldBeSticky) {
            // Add sticky class when element is fully off-screen
            if (!this.isSticky) {
              this.activateSticky();
            }

            // Trigger show/hide logic based on scroll direction
            if (scrollingDown) {
              if (this.showTimeout) {
                clearTimeout(this.showTimeout);
                this.showTimeout = null;
              }
              if (this.stickyParent && this.stickyParent.classList.contains('sticky-group-visible')) {
                this.stickyParent.classList.remove('sticky-group-visible');
              }
            } else {
              // Scrolling up - add visible class
              if (!this.showTimeout) {
                this.showTimeout = setTimeout(() => {
                  if (this.stickyParent) {
                    this.stickyParent.classList.add('sticky-group-visible');
                  }
                  this.showTimeout = null;
                }, this.showTimeoutDelay);
              }
            }

            // Update last RAF scroll position
            this.lastRafScrollTop = scrollTop;
          } else if (this.isSticky) {
            // Deactivate when scrolling back above element
            this.deactivateSticky();
          }
        }

        this.currentScrollTop = scrollTop;
        this.rafId = null;
      });
    }

    /**
     * Activates sticky behavior by adding the sticky class to parent.
     */
    activateSticky() {
      if (this.isSticky) return;

      try {
        this.isSticky = true;
        this.isVisible = true;
        if (this.stickyParent && this.stickyParent.classList) {
          // Add active class first (no transition yet)
          this.stickyParent.classList.add('sticky-group-active');

          // Add can-animate class after RAF to enable transitions
          requestAnimationFrame(() => {
            if (this.stickyParent && this.stickyParent.classList.contains('sticky-group-active')) {
              this.stickyParent.classList.add('sticky-group-can-animate');
            }
          });
        }
      } catch (error) {
        // Silently handle DOM manipulation errors
      }
    }


    /**
     * Deactivates sticky behavior by removing the sticky classes from parent and resetting transforms.
     */
    deactivateSticky() {
      if (!this.isSticky) return;

      try {
        this.isSticky = false;
        this.isVisible = false;
        if (this.stickyParent) {
          if (this.stickyParent.classList) {
            // Remove both active and can-animate classes together
            this.stickyParent.classList.remove('sticky-group-active', 'sticky-group-can-animate', 'sticky-group-visible');
          }
        }

        // Clear any pending show timeout
        if (this.showTimeout) {
          clearTimeout(this.showTimeout);
          this.showTimeout = null;
        }

        // Reset RAF debouncing
        this.lastRafScrollTop = this.getActualScrollTop();
      } catch (error) {
        // Silently handle cleanup errors
      }
    }

    // --------------------------------------------------------------------
    // UTILITIES
    // --------------------------------------------------------------------

    /**
     * Debounce utility to limit function execution frequency.
     * @param {Function} func - The function to debounce.
     * @param {number} wait - The delay in milliseconds.
     * @returns {Function} The debounced function.
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }

  customElements.define('sticky-group', StickyGroup);
}

